import { useMemo, type MouseEvent } from 'react';

import { radialPadLayout, radialPadTooltip } from './root/radialControlPad.constants.ts';
import { buildDualRingLayout } from './root/radialControlPad.functions.ts';
import { RadialPadRoot, RadialPadSvg, RadialTooltipLayer } from './root/radialControlPad.style.ts';
import type { RadialControlPadProps } from './root/radialControlPad.types.ts';
import { useDelayedSectorTooltip } from './root/useDelayedSectorTooltip.ts';
import { useRadialPadCenterContent } from './root/useRadialPadCenterContent.tsx';
import { useRadialPadRingContent } from './root/useRadialPadRingContent.tsx';

/**
 * Radial hold-to-repeat control pad with an inner ring and optional outer ring.
 * Layout geometry is adapted from Animate UI Radial Menu.
 * @see https://animate-ui.com/docs/components/community/radial-menu
 */
export const RadialControlPad = ({
  innerSectors,
  outerSectors,
  centerSectors,
  disabled,
  holdRepeatDelayMs,
  sectorGapPx = radialPadLayout.sectorGapPx,
  size,
}: RadialControlPadProps) => {
  const { tooltipSectorId, onHoveredSectorChange } = useDelayedSectorTooltip(
    radialPadTooltip.showDelayMs,
    radialPadTooltip.visibleMs,
    disabled
  );

  const hasInnerRing = innerSectors.length > 0;
  const resolvedCenterSectors = useMemo(
    () => centerSectors?.slice(0, radialPadLayout.maxCenterSectors),
    [centerSectors]
  );
  const hasCenterSectors = resolvedCenterSectors != null && resolvedCenterSectors.length > 0;
  const { innerRing, outerRing } = useMemo(
    () => buildDualRingLayout(innerSectors, outerSectors),
    [innerSectors, outerSectors]
  );

  const outermostRadius =
    outerRing?.outerRadius ??
    (hasInnerRing
      ? innerRing.outerRadius
      : hasCenterSectors
        ? radialPadLayout.ring.centerOuterRadius
        : radialPadLayout.ring.innerHoleRadius);
  const resolvedSize = size ?? outermostRadius * 2 + radialPadLayout.canvasMargin;
  const padRadius = resolvedSize / 2;

  const sharedRingProps = useMemo(
    () => ({
      padRadius,
      sectorGapPx,
      padDisabled: disabled,
      holdRepeatDelayMs,
      tooltipSectorId,
      onHoveredSectorChange,
    }),
    [padRadius, sectorGapPx, disabled, holdRepeatDelayMs, tooltipSectorId, onHoveredSectorChange]
  );

  const innerContent = useRadialPadRingContent({
    enabled: hasInnerRing,
    ring: innerRing,
    ...sharedRingProps,
  });
  const outerContent = useRadialPadRingContent({
    enabled: outerRing != null,
    ring: outerRing ?? innerRing,
    ...sharedRingProps,
  });
  const centerContent = useRadialPadCenterContent({
    enabled: hasCenterSectors,
    sectors: resolvedCenterSectors,
    ...sharedRingProps,
  });

  return (
    <RadialPadRoot
      $size={resolvedSize}
      aria-hidden={disabled}
      onDoubleClick={stopRadialPadDoubleClickPropagation}
    >
      <RadialPadSvg
        width={resolvedSize}
        height={resolvedSize}
        viewBox={`${-padRadius} ${-padRadius} ${resolvedSize} ${resolvedSize}`}
      >
        {outerContent?.svgNodes}
        {centerContent?.svgNodes}
        {innerContent?.svgNodes}
      </RadialPadSvg>
      <RadialTooltipLayer>
        {outerContent?.tooltipNodes}
        {centerContent?.tooltipNodes}
        {innerContent?.tooltipNodes}
      </RadialTooltipLayer>
    </RadialPadRoot>
  );
};

const stopRadialPadDoubleClickPropagation = (event: MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};
